import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import { 
  Keyboard, 
  Home,
  Swords, 
  Trophy, 
  User, 
  Crown,
  Menu,
  X,
  LogIn,
  Monitor
} from 'lucide-react';
import Auth from '../pages/Auth';
import DonationButton from './DonationButton';
import DonationModal from './DonationModal';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { isDesktop } = useDeviceDetection();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const { state: userState } = useUser();
  
  const isAuthenticated = userState.isAuthenticated;
  const user = userState.user;

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/typing', label: 'Type', icon: Keyboard },
    { path: '/battle', label: 'Battle', icon: Swords },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
    { path: '/premium', label: 'Premium', icon: Crown },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="bg-primary border-b border-secondary">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
              <Keyboard className="w-5 h-5 text-primary" />
            </div>
            <span className="text-xl font-bold text-accent group-hover:text-yellow-400 transition-colors">
              TypeRush
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-accent text-primary font-medium'
                      : 'text-text hover:text-accent hover:bg-secondary'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Auth and Profile buttons */}
          <div className="flex items-center space-x-2">
            {/* Donation Button */}
            <div className="hidden md:block">
              <DonationButton 
                variant="heart" 
                size="sm"
                className="text-sm"
                onClick={() => setIsDonationModalOpen(true)}
              />
            </div>
            
            {/* Login button or Profile */}
            {isAuthenticated ? (
              <Link 
                to="/profile" 
                className={`hidden md:flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all ${
                  isActive("/profile")
                    ? 'bg-accent text-primary'
                    : 'text-text hover:text-accent hover:bg-secondary'
                }`}
              >
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-accent/20">
                  <User className="w-4 h-4" />
                </div>
                {user && (
                  <span className="font-medium">{user.username}</span>
                )}
              </Link>
            ) : (
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="hidden md:flex items-center space-x-1 px-3 py-1.5 rounded-lg text-text hover:text-accent hover:bg-secondary transition-all"
              >
                <LogIn className="w-4 h-4" />
                <span>Login</span>
              </button>
            )}
            
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-text hover:text-accent hover:bg-secondary transition-all"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-secondary animate-fade-in">
            {isDesktop ? (
              // Show normal navigation for desktop
              <div className="flex flex-col space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      isActive(item.path)
                        ? 'bg-accent text-primary font-medium'
                        : 'text-text hover:text-accent hover:bg-secondary'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive("/profile")
                    ? 'bg-accent text-primary font-medium'
                    : 'text-text hover:text-accent hover:bg-secondary'
                }`}
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent/20">
                  <User className="w-4 h-4 text-accent" />
                </div>
                <span>{isAuthenticated && user ? user.username : 'Profile'}</span>
              </Link>
              
              {/* Mobile Donation Button */}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsDonationModalOpen(true);
                }}
                className="flex items-center space-x-3 px-4 py-3 rounded-lg text-text hover:text-red-500 hover:bg-secondary transition-all w-full text-left"
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-500/20">
                  <span className="text-red-500">❤️</span>
                </div>
                <span>Support Us</span>
              </button>
              
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsAuthModalOpen(true);
                }}
                className="flex items-center space-x-3 px-4 py-3 rounded-lg text-text hover:text-accent hover:bg-secondary transition-all w-full text-left"
              >
                <LogIn className="w-5 h-5" />
                <span>Login</span>
              </button>
            </div>            ) : (
              // Show desktop-only message for mobile/tablet devices
              <div className="px-4 py-8 text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center">
                    <Monitor className="w-8 h-8 text-accent" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-accent mb-2">Desktop Only</h3>
                <p className="text-textMuted mb-4">
                  TypeRush is designed for desktop users with physical keyboards.
                </p>
                <p className="text-sm text-textMuted">
                  Please visit us from a desktop or laptop computer to access the full typing experience.
                </p>
              </div>            )}
          </div>
        )}

        {/* Removed simplified Auth Modal since we're using the Auth component */}
      </div>
      
      {/* Auth Modal */}
      <Auth isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
      
      {/* Donation Modal */}
      <DonationModal 
        isOpen={isDonationModalOpen} 
        onClose={() => setIsDonationModalOpen(false)} 
      />
    </nav>
  );
};

export default Navigation;
